import React, { useEffect, useState } from "react";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Send, RefreshCw } from "lucide-react";

export default function AdminComunicaciones() {
  const { user } = useAuth();
  const superadmin = user?.roles?.includes("superadmin_dcc");
  const [notifs, setNotifs] = useState({ items: [], total: 0 });
  const [filter, setFilter] = useState({ status: "", tenant: "" });
  const [broadcast, setBroadcast] = useState({ tenant_slug: "banco-alfa", subject: "", body_html: "", body_text: "" });
  const [sending, setSending] = useState(false);

  const load = () => {
    const p = new URLSearchParams();
    if (filter.status) p.append("status", filter.status);
    if (filter.tenant) p.append("tenant", filter.tenant);
    p.append("limit", 50);
    api.get(`/admin/notifications?${p}`).then((r) => setNotifs(r.data));
  };
  useEffect(() => { load(); }, [filter.status, filter.tenant]);

  const retry = async (id) => {
    try {
      const { data } = await api.post(`/admin/notifications/${id}/retry`);
      toast.info(`Transport ${data.transport} · ${data.ok ? "OK" : "FAIL"}`);
      load();
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
  };

  const send = async (e) => {
    e.preventDefault(); setSending(true);
    try {
      const { data } = await api.post("/admin/broadcast", broadcast);
      toast.success(`Broadcast enviado a ${data.recipients} alumnos`);
      setBroadcast({ ...broadcast, subject: "", body_html: "", body_text: "" });
      load();
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <StaffLayout user={user} superadmin={superadmin}>
      <div>
        <p className="text-xs uppercase tracking-widest text-[#93C5FD] font-semibold">Comunicaciones</p>
        <h1 className="mt-2 text-3xl font-bold text-white font-display">Outbox global & broadcast</h1>

        <section className="mt-8 p-6 bg-[#111827] border border-gray-800 rounded-xl">
          <h2 className="text-lg font-semibold text-white mb-3">Enviar broadcast</h2>
          <form onSubmit={send} className="grid md:grid-cols-2 gap-3" data-testid="broadcast-form">
            <input required placeholder="Asunto *" value={broadcast.subject} onChange={(e) => setBroadcast({ ...broadcast, subject: e.target.value })}
              data-testid="bc-subject"
              className="bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white md:col-span-2" />
            <select value={broadcast.tenant_slug} onChange={(e) => setBroadcast({ ...broadcast, tenant_slug: e.target.value })}
              className="bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white">
              <option value="">Todos los tenants</option>
              <option value="banco-alfa">banco-alfa</option>
              <option value="demo-bank">demo-bank</option>
            </select>
            <input placeholder="Texto plano" value={broadcast.body_text} onChange={(e) => setBroadcast({ ...broadcast, body_text: e.target.value })}
              className="bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white" />
            <textarea required placeholder="HTML del correo" rows={3} value={broadcast.body_html} onChange={(e) => setBroadcast({ ...broadcast, body_html: e.target.value })}
              data-testid="bc-body"
              className="bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white md:col-span-2" />
            <button type="submit" disabled={sending} data-testid="bc-submit"
              className="inline-flex items-center justify-center gap-1 py-2.5 rounded-md bg-[#B84A0C] hover:bg-primary text-white font-semibold md:col-span-2 disabled:opacity-60">
              <Send className="w-4 h-4" /> {sending ? "Enviando..." : "Enviar broadcast"}
            </button>
          </form>
        </section>

        <section className="mt-8">
          <div className="flex items-center gap-2 mb-3">
            <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              className="bg-[#111827] border border-gray-800 rounded-md px-3 py-2 text-sm text-white">
              <option value="">Todos</option>
              <option value="sent">Enviados</option>
              <option value="failed">Fallidos</option>
              <option value="pending">Pendientes</option>
            </select>
            <span className="text-xs text-gray-500">{notifs.total} totales</span>
          </div>
          <div className="overflow-x-auto border border-gray-800 rounded-xl">
            <table className="w-full text-sm">
              <thead className="bg-[#0F172A] text-xs uppercase tracking-widest text-gray-400">
                <tr>
                  <th className="text-left px-3 py-2">Tipo</th>
                  <th className="text-left px-3 py-2">Título</th>
                  <th className="text-left px-3 py-2">Estado</th>
                  <th className="text-left px-3 py-2">Fecha</th>
                  <th className="text-right px-3 py-2">Acción</th>
                </tr>
              </thead>
              <tbody>
                {notifs.items.map((n) => (
                  <tr key={n.id} className="border-t border-gray-800">
                    <td className="px-3 py-2 font-mono text-xs text-[#93C5FD]">{n.type}</td>
                    <td className="px-3 py-2 text-white">{n.title}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded uppercase font-semibold ${
                        n.status === "sent" ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" :
                        n.status === "failed" ? "bg-red-500/10 text-red-300 border border-red-500/30" :
                        "bg-amber-500/10 text-amber-300 border border-amber-500/30"
                      }`}>{n.status}</span>
                    </td>
                    <td className="px-3 py-2 text-gray-500 font-mono text-xs">{new Date(n.created_at).toLocaleString("es-MX")}</td>
                    <td className="px-3 py-2 text-right">
                      {n.status !== "sent" && n.channel === "email" && (
                        <button onClick={() => retry(n.id)} className="text-xs text-[#93C5FD] hover:text-white inline-flex items-center gap-1">
                          <RefreshCw className="w-3 h-3" /> Reintentar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </StaffLayout>
  );
}
